// Cloudflare Worker API Client

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

// Token management
let accessToken: string | null = null;
let refreshToken: string | null = null;

// Load tokens from localStorage on init
if (typeof window !== 'undefined') {
  accessToken = localStorage.getItem('access_token');
  refreshToken = localStorage.getItem('refresh_token');
}

function setTokens(access: string, refresh: string) {
  accessToken = access;
  refreshToken = refresh;
  localStorage.setItem('access_token', access);
  localStorage.setItem('refresh_token', refresh);
}

function clearTokens() {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
}

// API Response types
interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

// Generic fetch wrapper with auth
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const url = `${API_URL}${endpoint}`;
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  if (accessToken) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
  }

  try {
    let response = await fetch(url, { ...options, headers });

    // Handle rate limiting (429)
    if (response.status === 429) {
      const retryAfter = response.headers.get('Retry-After') || '60';
      const data = await response.json();
      return { 
        success: false, 
        error: `Too many requests. Please wait ${retryAfter} seconds before trying again.`
      };
    }

    // If 401, try to refresh token
    if (response.status === 401 && refreshToken) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
        response = await fetch(url, { ...options, headers });
      }
    }

    const data = await response.json();
    return data as ApiResponse<T>;
  } catch (error) {
    console.error('API Error:', error);
    return { success: false, error: 'Network error' };
  }
}

async function refreshAccessToken(): Promise<boolean> {
  if (!refreshToken) return false;

  try {
    const response = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken })
    });

    const data = await response.json();
    
    if (data.success && data.data) {
      setTokens(data.data.access_token, data.data.refresh_token);
      return true;
    }
  } catch (error) {
    console.error('Token refresh failed:', error);
  }

  clearTokens();
  return false;
}

// Auth API
export const authApi = {
  async register(email: string, password: string, turnstileToken?: string) {
    const result = await apiFetch<{
      user: { id: string; email: string; role: string };
      access_token: string;
      refresh_token: string;
    }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, turnstile_token: turnstileToken })
    });

    if (result.success && result.data) {
      setTokens(result.data.access_token, result.data.refresh_token);
    }

    return result;
  },

  async login(email: string, password: string) {
    const result = await apiFetch<{
      user: { id: string; email: string; role: string };
      access_token: string;
      refresh_token: string;
    }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });

    if (result.success && result.data) {
      setTokens(result.data.access_token, result.data.refresh_token);
    }

    return result;
  },

  async logout() {
    await apiFetch('/api/auth/logout', { method: 'POST' });
    clearTokens();
  },

  async getMe() {
    return apiFetch<{
      id: string;
      email: string;
      role: 'user' | 'admin';
      created_at: string;
    }>('/api/auth/me');
  },

  isAuthenticated() {
    return !!accessToken;
  },

  getAccessToken() {
    return accessToken;
  },

  async resendVerification() {
    return apiFetch<{ message: string }>('/api/auth/resend-verification', {
      method: 'POST'
    });
  }
};

// Reports API
export const reportsApi = {
  async search(phoneNumber: string) {
    return apiFetch<{
      reports: FraudReport[];
      phone_activity: PhoneActivityTimeline | null;
    }>(`/api/reports/search?phone=${encodeURIComponent(phoneNumber)}`);
  },

  async getAll(params?: { status?: string; page?: number; limit?: number }) {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.page) query.set('page', params.page.toString());
    if (params?.limit) query.set('limit', params.limit.toString());
    
    return apiFetch<{
      reports: FraudReport[];
      pagination: { page: number; limit: number; total: number; total_pages: number };
    }>(`/api/reports?${query}`);
  },

  async getMy() {
    return apiFetch<FraudReport[]>('/api/reports/my');
  },

  async getById(id: number) {
    return apiFetch<FraudReport>(`/api/reports/${id}`);
  },

  async create(report: CreateReportData) {
    return apiFetch<{ id: number; message: string }>('/api/reports', {
      method: 'POST',
      body: JSON.stringify(report)
    });
  },

  async updateStatus(id: number, status: 'approved' | 'rejected') {
    return apiFetch<{ message: string }>(`/api/reports/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    });
  },

  async delete(id: number) {
    return apiFetch<{ message: string }>(`/api/reports/${id}`, {
      method: 'DELETE'
    });
  }
};

// Stats API
export const statsApi = {
  async getPublic() {
    return apiFetch<{
      total_reports: number;
      approved_reports: number;
      high_risk_numbers: number;
      total_users: number;
      total_amount_reported: number;
      prevented_frauds: number;
    }>('/api/stats/public');
  },

  async getAdmin() {
    return apiFetch<AdminStats>('/api/stats/admin');
  },

  async getUser() {
    return apiFetch<UserStats>('/api/stats/user');
  },

  async getTrending() {
    return apiFetch<TrendingStats>('/api/stats/trending');
  },

  async getHeatmap() {
    return apiFetch<HeatmapStats>('/api/stats/heatmap');
  }
};

// Users API (admin only)
export const usersApi = {
  async getAll(params?: { page?: number; limit?: number }) {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', params.page.toString());
    if (params?.limit) query.set('limit', params.limit.toString());
    
    return apiFetch<{
      users: User[];
      pagination: { page: number; limit: number; total: number; total_pages: number };
    }>(`/api/users?${query}`);
  },

  async updateRole(id: string, role: 'user' | 'admin') {
    return apiFetch<{ message: string }>(`/api/users/${id}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role })
    });
  },

  async delete(id: string) {
    return apiFetch<{ message: string }>(`/api/users/${id}`, {
      method: 'DELETE'
    });
  }
};

// Upload API
export const uploadApi = {
  async upload(file: File, reportId: number) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('report_id', reportId.toString());

    const response = await fetch(`${API_URL}/api/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      },
      body: formData
    });

    return response.json();
  },

  getFileUrl(fileKey: string) {
    return `${API_URL}/api/upload/file/${fileKey}`;
  },

  async getFileBlob(fileKey: string): Promise<string | null> {
    try {
      const response = await fetch(`${API_URL}/api/upload/file/${fileKey}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      if (!response.ok) return null;
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  },

  async getReportFiles(reportId: number) {
    return apiFetch<Media[]>(`/api/upload/report/${reportId}`);
  },

  async delete(id: number) {
    return apiFetch<{ message: string }>(`/api/upload/${id}`, {
      method: 'DELETE'
    });
  }
};

// Types (matching backend)
export interface User {
  id: string;
  email: string;
  role: 'user' | 'admin';
  created_at: string;
}

export interface FraudReport {
  id: number;
  user_id: string;
  title: string;
  phone_number: string;
  medium: string;
  transaction_method: string | null;
  description: string;
  division: string;
  district: string;
  upazila: string | null;
  amount_involved: number | null;
  victim_count: number;
  fraud_category: string;
  urgency_level: string;
  evidence_quality: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
  user_email?: string;
}

export interface PhoneActivityTimeline {
  id: number;
  phone_number: string;
  status: 'low-risk' | 'moderate-risk' | 'high-risk';
  total_reports: number;
  confirmed_reports: number;
  disputed_reports: number;
  risk_score: number;
  first_reported: string;
  last_reported: string;
}

export interface Media {
  id: number;
  report_id: number;
  file_key: string;
  file_name: string;
  file_type: string;
  file_size: number;
  created_at: string;
}

export interface CreateReportData {
  title: string;
  phone_number: string;
  medium: string;
  transaction_method?: string;
  description: string;
  division: string;
  district: string;
  upazila?: string;
  amount_involved?: number;
  victim_count?: number;
  fraud_category: string;
  urgency_level: string;
  evidence_quality: string;
}

export interface AdminStats {
  total_reports: number;
  pending_reports: number;
  approved_reports: number;
  rejected_reports: number;
  high_risk_numbers: number;
  total_users: number;
  admin_users: number;
  total_amount_reported: number;
  recent_activity: number;
  category_breakdown: { fraud_category: string; count: number }[];
  division_breakdown: { division: string; count: number }[];
  district_breakdown: { district: string; division: string; count: number }[];
  daily_reports: { date: string; count: number }[];
}

export interface UserStats {
  total_reports: number;
  pending_reports: number;
  approved_reports: number;
  rejected_reports: number;
  total_amount_reported: number;
  recent_activity: number;
  approval_rate: number;
}

export interface TrendingNumber {
  phone_number: string;
  report_count: number;
  primary_category: string;
  primary_location: string;
  total_amount: number;
  first_reported: string;
  last_reported: string;
}

export interface TrendingCategory {
  fraud_category: string;
  count: number;
  percentage: number;
}

export interface TrendingStats {
  trending_numbers: TrendingNumber[];
  trending_categories: TrendingCategory[];
  this_week_reports: number;
  last_week_reports: number;
  weekly_change: number;
}

export interface DistrictHeatmapData {
  district: string;
  division: string;
  report_count: number;
  total_amount: number;
  verified_count: number;
}

export interface HotspotData {
  district: string;
  division: string;
  report_count: number;
  categories: string;
}

export interface HeatmapStats {
  district_data: DistrictHeatmapData[];
  hotspots: HotspotData[];
}
